import { useEffect, useMemo, useRef, useState } from "react";
import { Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe, type StripePaymentElementChangeEvent } from "@stripe/stripe-js";
import { IconMapPin, IconReceipt2 } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { CheckboxField } from "../../../../components/common/checkbox-field";
import { PaymentViewSkeleton } from "./payment-view-skeleton";
import { CardDetailsSection } from "./card-details-section";
import { CheckoutStepper } from "./checkout-stepper";
import { PaymentMethodSection, type PaymentMethodId } from "./payment-method-section";
import { BasketOrderSummary } from "./basket-order-summary";
import { PaymentProcessingOverlay } from "./payment-processing-overlay";
import { SaleShippingAddressSection } from "./sale-shipping-address-section";
import { SaleBillingAddressSection } from "./sale-billing-address-section";
import { CheckoutBlockedView } from "./checkout-blocked-view";
import { AddressBookSection } from "../../../addresses/components/address-book-section";
import { useCartStore } from "../../../../hooks/stores/useCartStore";
import { useSaleDetails } from "../../../sales/hooks/useSaleDetails";
import { usePaymentIntent } from "../../hooks/usePaymentIntent";
import { useUpdateSaleBillingAddress } from "../../hooks/useUpdateSaleBillingAddress";
import { useCheckoutStore } from "../../domain/useCheckoutStore";
import { salesService } from "../../../api_client";
import {
  type CreateIntentDtoCurrencyEnum,
  type QuotationOrderLineDto,
  type SaleDetailsDto,
} from "../../../../api";
import type { CartItem } from "../../../../hooks/stores/useCartStore";
import { getProductUnitPrice } from "../../../products/utils/get-pricelist-price";
import { showErrorToast } from "../../../../utils/toasts";
import { Banner } from "../../../../components/common/banner";
import { toCheckoutError, type CheckoutError } from "../../utils/checkout-error";
import { getSaleDeliveryAddressId } from "../../utils/sale-delivery-address";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "");

interface PaymentViewProps {
  /**
   * Existing-quotation path: when a saleId is passed in the URL we're paying
   * for an already-created SO and do NOT create a new one. The cart-driven
   * path (no saleId prop) creates the draft SO up-front via
   * `useEnsureDraftSaleOrder` below before requesting the PaymentIntent.
   */
  saleId?: number | null;
  /** Called once payment confirms, with the saleId we charged for. */
  onSuccess: (saleId: number) => void;
  /** Cart path: back to the delivery step, e.g. when the address was refused. */
  onChangeDeliveryAddress: () => void;
}

function toOrderLines(
  items: CartItem[],
): QuotationOrderLineDto[] {
  // warrantyProductId is exposed by the backend DTO but won't appear on the
  // generated client until `npm run generate:api` runs after the BE module
  // ships. Cast disappears after regen.
  return items.map((i) => ({
    productId: i.product.id,
    quantity: i.quantity,
    displayName: i.product.displayName ?? i.product.name ?? undefined,
    unitPrice: getProductUnitPrice(i.product, i.quantity),
    ...(i.warranty ? { warrantyProductId: i.warranty.productId } : {}),
  })) as QuotationOrderLineDto[];
}

/**
 * Creates the draft sale order up-front when we're on the cart-driven path
 * (no externally-passed saleId). The id rides PaymentIntent metadata; the
 * Stripe webhook reads it on `payment_intent.succeeded` and confirms the SO.
 *
 * Why up-front and not after payment: the previous "create after payment"
 * path could leave money taken with no SO when the FE call failed (network,
 * tab close, etc) or the webhook was offline. Doing it before the card is
 * hit guarantees money charged ↔ exactly one SO exists.
 *
 * Idempotent within a tab session via `createdRef`. Re-mounting the page or
 * navigating back/forward in the stepper will create *another* draft —
 * cleaned up by the abandoned-draft cron (see TODO_IMPROVEMENTS.md).
 *
 * The bill-to isn't sent here: the buyer picks it on this same step, after
 * the draft exists, so `PaymentForm` writes it onto the order right before
 * charging the card.
 */
function useEnsureDraftSaleOrder({
  enabled,
  orderLines,
  shippingAddressId,
  loyaltyPointsToRedeem,
}: {
  enabled: boolean;
  orderLines: QuotationOrderLineDto[];
  shippingAddressId: number | null;
  loyaltyPointsToRedeem: number | null;
}): { draftSaleId: number | null; isCreating: boolean; error: CheckoutError | null } {
  const [draftSaleId, setDraftSaleId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<CheckoutError | null>(null);
  const createdRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (createdRef.current) return;
    if (orderLines.length === 0) return;

    createdRef.current = true;
    setIsCreating(true);
    setError(null);

    salesService
      .createQuotation({
        orderLines,
        ...(shippingAddressId != null && { shippingAddressId }),
        // `loyaltyPointsToRedeem` lands on `CreateQuotationDto` server-side;
        // generated client picks it up after `npm run generate:api`.
        ...(loyaltyPointsToRedeem != null && loyaltyPointsToRedeem > 0
          ? ({ loyaltyPointsToRedeem } as Record<string, number>)
          : {}),
      })
      .then((response) => {
        setDraftSaleId(response.data.id);
        setIsCreating(false);
      })
      .catch((err: unknown) => {
        setIsCreating(false);
        setError(toCheckoutError(err, "Could not start your order. Please try again."));
        // Allow a retry after failure (e.g. transient 5xx).
        createdRef.current = false;
      });
  }, [enabled, orderLines, shippingAddressId, loyaltyPointsToRedeem]);

  return { draftSaleId, isCreating, error };
}

/**
 * Step 3 of the checkout stepper. Wraps the inner form in Stripe's
 * `<Elements>` provider once the BE has handed us a `client_secret`.
 *
 * Order of operations for the cart-driven path:
 *   1. Create the draft SO (`useEnsureDraftSaleOrder`).
 *   2. Request the PaymentIntent for that SO id.
 *   3. User confirms with Stripe.
 *   4. Webhook confirms the SO server-side.
 *   5. Confirmation page shows "processing" until state flips, then receipt.
 */
export function PaymentView({ saleId, onSuccess, onChangeDeliveryAddress }: PaymentViewProps) {
  const { items } = useCartStore();
  const { shippingAddressId, loyaltyPointsToRedeem, checkoutItems } = useCheckoutStore();
  const { data: sale, isLoading: isLoadingSale } = useSaleDetails(saleId ?? null);

  const checkoutItemsSource = checkoutItems ?? items;
  const orderLines = useMemo(
    () => toOrderLines(checkoutItemsSource),
    [checkoutItemsSource],
  );
  const { draftSaleId, isCreating: isCreatingDraft, error: draftError } =
    useEnsureDraftSaleOrder({
      enabled: saleId == null,
      orderLines,
      shippingAddressId,
      loyaltyPointsToRedeem,
    });

  const effectiveSaleId = saleId ?? draftSaleId;

  // After the draft SO exists, fetch its details so the PaymentIntent total
  // reflects whatever the BE actually persisted — including the loyalty
  // discount line. Avoids the FE having to mirror the BE's pricing model
  // (taxes, discounts, future per-line adjustments) twice.
  const { data: draftSale, isLoading: isLoadingDraftSale } = useSaleDetails(
    saleId == null ? draftSaleId : null,
  );
  const effectiveSale = sale ?? draftSale;

  const totalAmount = Math.round((effectiveSale?.amountTotal ?? 0) * 100);
  const currency = (
    effectiveSale?.currency?.name?.toLowerCase() ?? "qar"
  ) as CreateIntentDtoCurrencyEnum;

  // An existing quotation may have no delivery address yet (e.g. created via
  // "Request quotation"). The BE won't take payment for it, so hold the
  // intent back and let the buyer pick one first.
  const needsDeliveryAddress =
    saleId != null && sale != null && getSaleDeliveryAddressId(sale) == null;

  const {
    clientSecret,
    isLoading: isLoadingIntent,
    error: intentError,
    retry: retryIntent,
  } = usePaymentIntent(totalAmount, currency, needsDeliveryAddress ? null : effectiveSaleId);
  const blockingError = draftError ?? intentError;

  // Paying an existing quotation: there's no delivery step to go back to, so
  // when the BE refuses the intent over the order's ship-to or bill-to (e.g.
  // archived, incomplete — checks the FE can't fully mirror) let the buyer
  // fix it right here, then ask for the intent again.
  const refusedAddress = saleId != null ? intentError?.address : undefined;

  const elementsOptions = useMemo(
    () => (clientSecret ? { clientSecret } : undefined),
    [clientSecret],
  );

  const intentReady =
    !!clientSecret &&
    !isLoadingIntent &&
    !isCreatingDraft &&
    effectiveSaleId != null &&
    !isLoadingDraftSale &&
    (!saleId || !isLoadingSale);

  return (
    <div className="flex w-full flex-col gap-6 px-8 py-6">
      <CheckoutStepper current={2} />

      {needsDeliveryAddress || refusedAddress === "delivery" ? (
        <div className="flex flex-col gap-4 lg:w-2/3">
          <Banner tone="warning" icon={<IconMapPin size={16} />}>
            {needsDeliveryAddress
              ? "This order has no delivery address yet. Choose one to continue to payment."
              : intentError?.message}
          </Banner>
          <SaleShippingAddressSection
            saleId={saleId!}
            startEditing={!needsDeliveryAddress}
            onSaved={intentError ? retryIntent : undefined}
          />
        </div>
      ) : refusedAddress === "billing" ? (
        <div className="flex flex-col gap-4 lg:w-2/3">
          <Banner tone="warning" icon={<IconReceipt2 size={16} />}>
            {intentError?.message}
          </Banner>
          <SaleBillingAddressSection saleId={saleId!} onSaved={retryIntent} />
        </div>
      ) : blockingError ? (
        <CheckoutBlockedView
          error={blockingError}
          onChangeDeliveryAddress={saleId == null ? onChangeDeliveryAddress : undefined}
        />
      ) : !intentReady ? (
        <PaymentViewSkeleton />
      ) : (
        <Elements stripe={stripePromise} options={elementsOptions}>
          <PaymentForm
            saleId={effectiveSaleId!}
            isExistingSale={saleId != null}
            sale={saleId != null ? effectiveSale : undefined}
            checkoutItems={checkoutItemsSource}
            onSuccess={onSuccess}
          />
        </Elements>
      )}
    </div>
  );
}

interface PaymentFormProps {
  saleId: number;
  isExistingSale: boolean;
  sale?: SaleDetailsDto;
  checkoutItems: CartItem[];
  onSuccess: (saleId: number) => void;
}

type Stage = "idle" | "confirming" | "done";

function PaymentForm({ saleId, isExistingSale, sale, checkoutItems, onSuccess }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { clearCart } = useCartStore();
  const {
    billingSameAsShipping,
    setBillingSameAsShipping,
    billingAddressId,
    setBillingAddressId,
    reset: resetCheckout,
  } = useCheckoutStore();

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodId>("card");
  const [cardholderName, setCardholderName] = useState("");
  const [stage, setStage] = useState<Stage>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { update: updateBillingAddress } = useUpdateSaleBillingAddress();

  const isProcessing = stage !== "idle";

  // Paying an existing quotation that already bills a separate address:
  // start the billing picker on it, so "Place Order" doesn't silently reset
  // the bill-to to the company.
  const invoicePartnerId = sale?.invoicePartner?.id ?? null;
  const companyPartnerId = sale?.partner?.id ?? null;
  useEffect(() => {
    if (invoicePartnerId == null || invoicePartnerId === companyPartnerId) return;
    setBillingSameAsShipping(false);
    setBillingAddressId(invoicePartnerId);
  }, [invoicePartnerId, companyPartnerId, setBillingSameAsShipping, setBillingAddressId]);

  // A message left by the last "Place Order" (e.g. "Your card number is
  // incomplete.") describes the card as it was then — drop it as soon as
  // the buyer edits the card, or it lingers after the card is complete.
  const handleCardChange = (event: StripePaymentElementChangeEvent) => {
    if (!event.empty) setErrorMessage(null);
  };

  const handlePlaceOrder = async () => {
    if (!stripe || !elements) {
      showErrorToast("Stripe is not ready yet. Please try again in a moment.");
      return;
    }
    if (paymentMethod !== "card") {
      // Other methods (bank/pay later/cash) aren't wired into the PSP yet —
      // see TODO_IMPROVEMENTS.md. UI shows them as Coming soon.
      showErrorToast("This payment method is not available yet.");
      return;
    }
    if (!billingSameAsShipping && billingAddressId == null) {
      showErrorToast("Please select or add a billing address.");
      return;
    }
    const trimmedCardholder = cardholderName.trim();
    if (!trimmedCardholder) {
      // The PaymentElement is configured with `fields.billingDetails.name =
      // "never"` (see card-details-section), which means Stripe expects us
      // to pass the cardholder name in confirmParams ourselves. Without it,
      // Stripe throws an IntegrationError at confirmPayment time.
      showErrorToast("Please enter the cardholder name.");
      return;
    }

    setErrorMessage(null);
    setStage("confirming");

    const submit = await elements.submit();
    if (submit.error) {
      setStage("idle");
      setErrorMessage(submit.error.message ?? "Please check your card details.");
      return;
    }

    try {
      await updateBillingAddress(saleId, billingSameAsShipping ? null : billingAddressId);
    } catch (err) {
      setStage("idle");
      setErrorMessage(toCheckoutError(err, "Could not save the billing address.").message);
      return;
    }

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams: {
        payment_method_data: {
          billing_details: { name: trimmedCardholder },
        },
      },
    });

    if (error) {
      setStage("idle");
      setErrorMessage(error.message ?? "Payment failed. Please try again.");
      return;
    }

    if (paymentIntent && paymentIntent.status !== "succeeded") {
      // 3DS / async confirmation paths — see TODO_IMPROVEMENTS.md.
      setStage("idle");
      setErrorMessage(
        "Payment is still processing. We'll email you once it's confirmed.",
      );
      return;
    }

    // Stripe confirmed the charge. The webhook will confirm the Odoo SO
    // server-side; the FE no longer creates / mutates the SO here.
    // Set stage to "done" first (shows success animation), then navigate
    // to confirmation BEFORE clearing the cart so the empty-cart guard
    // doesn't race us to /products.
    setStage("done");
    window.setTimeout(() => {
      onSuccess(saleId);
      clearCart();
      resetCheckout();
    }, 600);
  };

  return (
    <>
      {/*
        `items-start` pins both columns to the top of the grid row instead of
        CSS Grid's default `stretch` so collapsing the billing book doesn't
        leave phantom whitespace below the (now-shorter) left column.
      */}
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
        <div className="flex flex-col gap-6 lg:col-span-2">
          {isExistingSale && <SaleShippingAddressSection saleId={saleId} />}

          <div className="flex flex-col gap-3">
            <Heading variant="h3" tone="dark">Payment Method</Heading>
            <PaymentMethodSection selected={paymentMethod} onChange={setPaymentMethod} />
          </div>

          {paymentMethod === "card" && (
            <CardDetailsSection
              cardholderName={cardholderName}
              onCardholderNameChange={setCardholderName}
              onCardChange={handleCardChange}
            />
          )}

          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <Heading variant="h3" tone="dark">Billing address</Heading>
              <CheckboxField
                checked={billingSameAsShipping}
                onChange={(value) => {
                  setBillingSameAsShipping(value);
                  if (value) setBillingAddressId(null);
                }}
                label="Same as delivery address"
                variant="inline"
              />
            </div>
            {!billingSameAsShipping && (
              <AddressBookSection
                type="invoice"
                heading=""
                selectedId={billingAddressId}
                onSelect={setBillingAddressId}
                autoSelectFirst={false}
              />
            )}
          </div>

          {errorMessage && (
            <Text variant="body" tone="inherit" className="text-accentError">
              {errorMessage}
            </Text>
          )}

          <Button
            variant="primary"
            fullWidth
            onClick={handlePlaceOrder}
            disabled={isProcessing}
          >
            {isProcessing ? "Processing…" : "Place Order"}
          </Button>
        </div>

        <div className="lg:col-span-1">
          <BasketOrderSummary
            sale={sale}
            items={!isExistingSale ? checkoutItems : undefined}
            onProceed={handlePlaceOrder}
            buttonLabel={isProcessing ? "Processing…" : "Place Order"}
          />
        </div>
      </div>

      {isProcessing && (
        <PaymentProcessingOverlay stage={stage as Exclude<Stage, "idle">} />
      )}
    </>
  );
}
